import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  Search,
  Activity,
  Stethoscope,
  Pill,
  Receipt,
  ClipboardList,
  ChevronDown,
  LogOut,
  MapPin,
  Users,
  Monitor,
  BedDouble,
  Package,
  UploadCloud,
  Shield,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef, useEffect } from "react";
import { BrandLogo } from "@/components/BrandLogo";

const ROLE_LABEL = {
  admin: "Front Office",
  nurse: "Nursing",
  doctor: "Doctor",
  pharmacy: "Pharmacy",
  billing: "Pharmacy Billing",
  ip: "IP Department",
  manager: "Manager",
  marketing: "Marketing",
  superadmin: "Superadmin",
};

const NAV_BY_ROLE = {
  admin: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/patients/new", label: "Register Patient", icon: UserPlus, feature: "patient_register" },
    { to: "/patients", label: "Patient Search", icon: Search },
    { to: "/patients/bulk", label: "Bulk Import", icon: UploadCloud, feature: "patient_bulk_import" },
  ],
  nurse: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/vitals", label: "Vitals Queue", icon: Activity, feature: "vitals_entry" },
    { to: "/patients", label: "Patients", icon: Search },
  ],
  doctor: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/consultation", label: "Consultation", icon: Stethoscope, feature: "consultation" },
    { to: "/patients", label: "Patients", icon: Search },
  ],
  pharmacy: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/dispense", label: "Dispensary", icon: Pill },
    { to: "/inventory", label: "Stock", icon: Package, feature: "inventory_edit" },
    { to: "/patients", label: "Patients", icon: Search },
  ],
  billing: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/billing", label: "Pharmacy Billing", icon: Receipt, feature: "pharmacy_billing" },
    { to: "/billing/direct", label: "Direct Bill", icon: Receipt, feature: "direct_bill" },
    { to: "/inventory", label: "Stock", icon: Package, feature: "inventory_edit" },
    { to: "/patients", label: "Patients", icon: Search },
  ],
  ip: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/patients", label: "Patient Search", icon: Search },
    { to: "/manager/ip", label: "IP Admissions & Rooms", icon: BedDouble, feature: "ip_admission" },
    { to: "/manager/therapists", label: "Therapists", icon: Users },
    { to: "/billing", label: "Pharmacy Billing", icon: Receipt, feature: "pharmacy_billing" },
    { to: "/manager/insurance", label: "Insurance Claims", icon: Shield, feature: "insurance_validate" },
  ],
  manager: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/manager/ip", label: "In-patients & Rooms", icon: BedDouble, feature: "ip_admission" },
    { to: "/manager/insurance", label: "Insurance Claims", icon: Shield, feature: "insurance_validate" },
    { to: "/manager/therapists", label: "Therapists", icon: Users },
    { to: "/manager/reports", label: "End-of-Day Report", icon: ClipboardList, feature: "reports_eod" },
    { to: "/inventory", label: "Stock", icon: Package, feature: "inventory_edit" },
    { to: "/patients", label: "Patients", icon: Search },
    { to: "/patients/bulk", label: "Bulk Import", icon: UploadCloud, feature: "patient_bulk_import" },
    { to: "/billing/direct", label: "Direct Bill", icon: Receipt, feature: "direct_bill" },
    { to: "/accounts/direct-bills", label: "Accounts · Tally review", icon: ClipboardList },
    { to: "/marketing/followups", label: "Marketing · Follow-ups", icon: ClipboardList },
  ],
  marketing: [
    { to: "/marketing/followups", label: "Follow-ups", icon: ClipboardList },
    { to: "/patients", label: "Patients", icon: Search },
  ],
  superadmin: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/patients/new", label: "Register Patient", icon: UserPlus, feature: "patient_register" },
    { to: "/patients", label: "Patient Search", icon: Search },
    { to: "/patients/bulk", label: "Bulk Import", icon: UploadCloud, feature: "patient_bulk_import" },
    { to: "/vitals", label: "Vitals Queue", icon: Activity, feature: "vitals_entry" },
    { to: "/consultation", label: "Consultation", icon: Stethoscope, feature: "consultation" },
    { to: "/billing", label: "Pharmacy Billing", icon: Receipt, feature: "pharmacy_billing" },
    { to: "/billing/direct", label: "Direct Bill", icon: Receipt, feature: "direct_bill" },
    { to: "/dispense", label: "Dispensary", icon: Pill },
    { to: "/inventory", label: "Stock", icon: Package, feature: "inventory_edit" },
    { to: "/manager/ip", label: "In-patients & Rooms", icon: BedDouble, feature: "ip_admission" },
    { to: "/manager/therapists", label: "Therapists", icon: Users },
    { to: "/manager/reports", label: "End-of-Day Report", icon: ClipboardList, feature: "reports_eod" },
    { to: "/manager/insurance", label: "Insurance Claims", icon: Shield, feature: "insurance_validate" },
    { to: "/accounts/direct-bills", label: "Accounts · Tally review", icon: ClipboardList },
    { to: "/marketing/followups", label: "Marketing · Follow-ups", icon: ClipboardList },
    { to: "/admin/vault", label: "Cross-branch Vault", icon: Shield },
    { to: "/admin/doctors", label: "Doctor Mapping", icon: Users },
    { to: "/admin/users", label: "User Management", icon: Shield },
  ],
};

export default function AppLayout() {
  const { user, branch, logout, setBranch } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const nav = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.admin;
  // Enforce per-user feature flags: if the flag is explicitly false, hide the
  // sidebar entry. Undefined = role default (visible).
  const filteredNav = nav.filter((item) => {
    if (!item.feature) return true;
    const flags = user?.feature_flags || {};
    return flags[item.feature] !== false;
  });

  const handleSwitchBranch = () => {
    setBranch(null);
    navigate("/select-branch");
  };

  return (
    <div className="min-h-screen bg-warm-sand flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-sand bg-white sticky top-0 h-screen overflow-hidden">
        <div className="px-6 py-6 border-b border-sand shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <BrandLogo size="h-11" />
          </Link>
          <FontSizeSwitcher />
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-1" data-testid="sidebar-nav">
          <div className="px-2 mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5652]">
            {ROLE_LABEL[user?.role] || "Workspace"}
          </div>
          {filteredNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                end
                className={({ isActive }) =>
                  `sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? "bg-forest text-white"
                      : "text-[#2A2624] hover:bg-[#F9F6F0]"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                {item.phase && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-[#E5A93D] bg-[#E5A93D]/10 px-1.5 py-0.5 rounded">
                    P{item.phase}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-sand space-y-2 shrink-0">
          <button
            onClick={() => window.open(`/queue?b=${branch?.code || ""}`, "_blank", "noopener")}
            data-testid="sidebar-open-queue-board"
            className="w-full text-left px-3 py-2.5 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-forest" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-forest">Open Queue Board</div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">New window</div>
              </div>
            </div>
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-2">
              Current branch
            </div>
            <button
              onClick={handleSwitchBranch}
              data-testid="sidebar-switch-branch"
              className="w-full text-left px-3 py-2.5 rounded-lg border border-sand bg-[#F9F6F0] hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-forest" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-forest truncate">
                    {branch?.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
                    {branch?.code} · switch
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-[#F9F6F0]/90 backdrop-blur border-b border-sand">
          <div className="px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
            <div className="md:hidden flex items-center gap-2">
              <BrandLogo size="h-8" />
            </div>
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C5652]">Branch</div>
              <div className="font-display text-forest text-lg leading-tight" data-testid="topbar-branch">
                {branch?.name}
              </div>
            </div>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="topbar-user-menu"
                className="flex items-center gap-2 h-10 pl-2 pr-3 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] transition-colors"
              >
                <div className="h-7 w-7 rounded-full bg-forest text-white flex items-center justify-center text-xs font-bold">
                  {user?.name?.slice(0, 1) || "U"}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-xs font-semibold text-[#2A2624] leading-tight">
                    {user?.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
                    {ROLE_LABEL[user?.role]}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-[#5C5652]" strokeWidth={1.5} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-sand rounded-lg overflow-hidden shadow-sm" data-testid="user-menu">
                  <div className="px-4 py-3 border-b border-sand">
                    <div className="text-xs font-semibold text-[#2A2624]">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
                      @{user?.username}
                    </div>
                  </div>
                  <button
                    onClick={handleSwitchBranch}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F9F6F0] flex items-center gap-2"
                    data-testid="menu-switch-branch"
                  >
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                    Switch branch
                  </button>
                  <button
                    onClick={logout}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F9F6F0] text-[#C25946] flex items-center gap-2"
                    data-testid="menu-logout"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-6 sm:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function FontSizeSwitcher() {
  const { fontSize, setFontSize, FONT_SIZES, user } = useAuth();
  return (
    <div className="mt-4" data-testid="font-size-switcher">
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#5C5652] mb-1.5">Text size</div>
      <div className="flex gap-1 bg-[#F9F6F0] border border-sand rounded-lg p-1">
        {FONT_SIZES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFontSize(s, user?.username)}
            data-testid={`font-size-${s}`}
            className={`flex-1 py-1 rounded-md font-bold transition-colors ${
              fontSize === s ? "bg-forest text-white" : "text-[#5C5652] hover:text-forest"
            }`}
            style={{ fontSize: { sm: "10px", md: "12px", lg: "14px", xl: "16px" }[s] }}
            title={`${s.toUpperCase()} text`}
          >
            A
          </button>
        ))}
      </div>
    </div>
  );
}
